import Chart from 'chart.js/auto';
import 'chartjs-adapter-date-fns';
import {deepAssign, hexToRgba} from "../utils";
import {nn} from "date-fns/locale";

function filterDates( dateList, range ) {
	const now = new Date();
	const today = new Date( now.getFullYear(), now.getMonth(), now.getDate() );
	let startDate = new Date( today );

	switch ( range ) {
		case 'one_month':
			startDate.setMonth( today.getMonth() - 1 );
			break;
		case 'six_months':
			startDate.setMonth( today.getMonth() - 6 );
			break;
		case 'ytd':
			startDate = new Date( today.getFullYear(), 0, 1 );
			break;
		case 'one_year':
			startDate.setFullYear( today.getFullYear() - 1 );
			break;
		case 'three_years':
			startDate.setFullYear( today.getFullYear() - 3 );
			break;
		case 'five_years':
			startDate.setFullYear( today.getFullYear() - 5 );
			break;
		default:
		case 'all':
			return dateList;
	}

	return dateList.filter( dateStr => {
		const itemDate = new Date( dateStr );
		return itemDate >= startDate && itemDate <= today;
	} );
}

const htmlLegendPlugin = {
	id: 'htmlLegend',
	afterUpdate( chart, args, options ) {
		const $legends = $( options.container );
		$legends.empty();

		const items = chart.options.plugins.legend.labels.generateLabels( chart );
		items.forEach( function( item, index ) {
			var color = item.fillStyle;
			if ( color instanceof CanvasGradient ) {
				color = chart.config.data.datasets[ index ].borderColor;
			}
			$legends.append( `
<li class="flex items-center gap-2">
	<span class="inline-block rounded-full aspect-square" style="width: 16px; height: 16px; background: ${color}"></span>
	<span class="top-0.5">${item.text}</span>
</li>
` );
		} );
	},
};

const chartAreaBorder = {
	id: 'chartAreaBorder',
	beforeDraw( chart, args, options ) {
		const {
			ctx,
			chartArea: {
				left,
				top,
				width,
				height,
			},
		} = chart;
		ctx.save();
		ctx.strokeStyle = options.borderColor;
		ctx.lineWidth = options.borderWidth;
		ctx.setLineDash( options.borderDash || [] );
		ctx.lineDashOffset = options.borderDashOffset;
		ctx.strokeRect( left, top, width, height );
		ctx.restore();
	},
};

window.initCharts = function( data ) {
	let chart = null;
	const period = data.settings.period;
	if ( period ) {
		document.getElementById( data.chartTabId )
			.querySelectorAll( '.period-selector-dropdown .dropdown-item' )
			.forEach( function( periodSelectorElement ) {
				periodSelectorElement.addEventListener( 'click', function( event ) {
					event.preventDefault();

					chart?.destroy();
					chart = createChart( JSON.parse( JSON.stringify( data ) ), this.dataset.period );
				} );
			} );
	}

	chart = createChart( JSON.parse( JSON.stringify( data ) ), period );
};

window.createChart = function( data, period ) {
	const { settings } = data;
	let { type } = data;
	let {
		datasets,
		labels,
	} = data.data;

	labels = filterDates( labels, period );
	datasets.forEach( ( { data } ) => data.slice( -labels.length ) );

	const canvas = document.getElementById( data.chartId );

	Chart.defaults.font.family = 'hedley-new-web';
	Chart.defaults.color = '#00123b';
	Chart.defaults.font.size = 16;

	var plugins = [chartAreaBorder];
	var options = {
		plugins: {
			legend: {
				display: false,
			},
			tooltip: {
				backgroundColor: 'rgba(0, 18, 59, .8)',
				padding: 10,
				caretSize: 0,
				boxWidth: 12,
				boxHeight: 12,
				boxPadding: 5,
				usePointStyle: true,
				titleFont: { weight: '400' },
				titleMarginBottom: 3,
				callbacks: {
					labelColor: function( context ) {
						var color = context.dataset.backgroundColor;
						if ( color instanceof Function ) {
							color = context.dataset.borderColor;
						} else if ( Array.isArray( color ) ) {
							color = color[ context.dataIndex ];
						}
						return {
							borderColor: color,
							backgroundColor: color,
							borderWidth: 0,
							borderRadius: 0,
						};
					},
					label: function( context ) {
						var result = context.formattedValue;
						if ( settings.percent ) {
							result += ' %';
						}
						return result;
					},
				},
			},
			chartAreaBorder: {
				borderColor: 'rgba(0,0,0,0)',
			},
		},
		layout: {
			padding: 0,
		},
		responsive: true,
		elements: {
			arc: {
				borderWidth: 0,
			},
			point: {
				hitRadius: 4,
				hoverRadius: 4,
				radius: 0,
			},
			line: {
				borderWidth: 2,
			},
		},
		hover: { mode: null },
		borderSkipped: 'middle',
	};

	if ( settings.legend ) {
		plugins.push( htmlLegendPlugin );
		deepAssign( options, {
			plugins: {
				htmlLegend: {
					container: document.getElementById( settings.legend.id ),
				},
			},
		} );
	}

	if ( type === 'bar' ) {
		deepAssign( options, {
			borderRadius: 3,
		} );
	}

	if ( type === 'stacked-bar' ) {
		type = 'bar';
		deepAssign( options, {
			scales: {
				x: {
					stacked: true,
				},
				y: {
					stacked: true,
				},
			},
			borderRadius: 5,
		} );
	}

	if ( type !== 'pie' ) {
		deepAssign( options, {
			scales: {
				x: {
					border: {
						display: false,
					},
					ticks: {
						display: settings.showXAxisLabel,
						color: '#7d879f',
					},
					grid: {
						display: false,
					},
				},
				y: {
					border: {
						display: false,
					},
					ticks: {
						display: settings.showYAxisLabel,
						color: '#7d879f',
						callback: function( value, index, values ) {
							return value + settings.yAxisSuffix + ' ';
						},
					},
					grid: {
						display: settings.showYAxisLines,
						tickLength: 0,
						color: '#dce0e7',
					},
				},
			},
		} );

		if ( labels.every( function( label ) {
			return typeof label === 'string' && label.match( /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/ );
		} ) ) {
			deepAssign( options, {
				scales: {
					x: {
						type: 'time',
						time: {
							tooltipFormat: 'd. MMM yyyy',
							displayFormats: {
								year: 'yyyy',
								quarter: "'Kv' Q yyyy",
								month: 'MMM yyyy',
								day: 'dd. MMM',
							},
						},
						adapters: {
							date: {
								// locale: nn,
							},
						},
					},
				},
			} );

			let timeAxisUnit = null;
			if ( period ) {
				switch ( period ) {
					case 'one_month':
						timeAxisUnit = 'day';
						break;
					case 'six_months':
					case 'one_year':
					case 'ytd':
						timeAxisUnit = 'month';
						break;
					default:
					case 'three_years':
					case 'five_years':
					case 'all':
						timeAxisUnit = 'year';
						break;
				}
			} else {
				switch ( settings.xAxisType ) {
					case 'yearly':
						timeAxisUnit = 'year';
						break;
					case 'quarterly':
						timeAxisUnit = 'quarter';
						break;
					case 'monthly':
						timeAxisUnit = 'month';
						break;
				}
			}
			if ( timeAxisUnit ) {
				deepAssign( options, {
					scales: {
						x: {
							time: {
								unit: timeAxisUnit,
							},
						},
					},
				} );
			}
		}
	}

	if ( type === 'line' ) {
		deepAssign( options, {
			plugins: {
				tooltip: {
					mode: 'index',
				},
			},
		} );
		datasets.forEach( function( dataset ) {
			dataset.fill = 'start';
			var color = dataset.backgroundColor;
			dataset.borderColor = color;
			dataset.backgroundColor = function( context ) {
				var chart = context.chart;
				var ctx = chart.ctx;
				var chartArea = chart.chartArea;

				if ( !chartArea ) {
					return; // This case happens on initial chart load
				}

				var gradient = ctx.createLinearGradient( 0, chartArea.top, 0, chartArea.top + chartArea.bottom );
				gradient.addColorStop( 0, hexToRgba( color, 0.32 ) );
				gradient.addColorStop( 0.5, hexToRgba( color, 0.32 ) );
				gradient.addColorStop( 1, hexToRgba( color, 0 ) );
				return gradient;
			};
		} );
	}

	return new Chart( canvas, {
		type: type,
		data: {
			labels,
			datasets,
		},
		options: options,
		plugins: plugins,
	} );
};
